import type { ActionPreviewLiveContent } from 'api/journeys/requests';
import { JourneyActionValuesTypes, TravellerJourneyActionDetailed } from 'api/types';

import {
  collectBrokenVariableLabels,
  recipientTokens,
  resolvedOrTemplateAddresses,
  resolvedOrTemplateSubject,
} from '../shared/preview-variables';

// Spread into each action card-menu's local locale so they share one definition.
export const previewEmailLocale = {
  en: { previewEmail: 'Preview email' },
};

export type PreviewEmailProps = {
  action: 'journey' | 'traveller';
  shortId: string;
  to: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  replyTo?: string | string[];
  subject?: string;
  attachments?: string[];
  sendFromEmailId?: string;
  // When set (preview opened from an editor), the body is rendered from this
  // unsaved draft instead of the saved template/snapshot.
  liveContent?: ActionPreviewLiveContent;
  // Labels of broken (deleted/severed source) variables, so the header-field
  // chips can render red to match the body.
  brokenLabels?: string[];
};

type AttachmentLike = { value: string; variables?: Record<string, { label: string }> };

export const getPreviewAttachments = (
  attachments: readonly AttachmentLike[] | undefined,
): string[] => {
  return (attachments ?? []).map((attachment) => {
    const mappedLabel = attachment.variables
      ? Object.values(attachment.variables)[0]?.label
      : undefined;
    return mappedLabel ?? attachment.value;
  });
};

export const buildEmailPreviewProps = (
  values: JourneyActionValuesTypes['send_email'] | undefined,
  shortId: string,
  noRecipientText: string,
  action: PreviewEmailProps['action'] = 'journey',
): PreviewEmailProps => ({
  action,
  shortId,
  to: values?.recipients ? recipientTokens(values.recipients) : noRecipientText,
  cc: recipientTokens(values?.cc),
  bcc: recipientTokens(values?.bcc),
  replyTo: recipientTokens(values?.replyTo),
  subject: resolvedOrTemplateSubject(undefined, values?.subject),
  attachments: getPreviewAttachments(values?.attachments),
  sendFromEmailId: values?.sendFromEmailId,
  brokenLabels: collectBrokenVariableLabels(
    [values?.recipients, values?.cc, values?.bcc, values?.replyTo],
    values?.subject,
  ),
});

// Prefers `action.data` (per-traveller resolver output) over `action.values`
// (the template) for fields that resolve per traveller.
export const buildEmailPreviewPropsFromTravellerAction = (
  action: TravellerJourneyActionDetailed<'send_email'>,
): PreviewEmailProps => ({
  action: 'traveller',
  shortId: action.shortId,
  to: resolvedOrTemplateAddresses(action.data?.recipients, action.values?.recipients),
  cc: resolvedOrTemplateAddresses(action.data?.cc, action.values?.cc),
  bcc: resolvedOrTemplateAddresses(action.data?.bcc, action.values?.bcc),
  replyTo: resolvedOrTemplateAddresses(action.data?.replyTo, action.values?.replyTo),
  subject: resolvedOrTemplateSubject(action.data?.subject, action.values?.subject),
  attachments: getPreviewAttachments(action.values?.attachments),
  sendFromEmailId: action.data?.sendFromEmailId || action.values?.sendFromEmailId,
  brokenLabels: collectBrokenVariableLabels(
    [action.values?.recipients, action.values?.cc, action.values?.bcc, action.values?.replyTo],
    action.values?.subject,
  ),
});
